/**
 * RequestTypeField Component
 * Field for selecting request type
 */

import React, { memo } from 'react';
import {
    TextField,
    MenuItem,
    FormControl,
    FormLabel,
    FormHelperText,
} from '@mui/material';
import { RequestType } from '@features/requests/types';
import { useRequestTranslations } from '@shared/i18n/hooks/useRequestTranslations';
import { useI18n } from '@shared/i18n';
import styles from './RequestTypeField.module.css';

/**
 * Props for RequestTypeField component
 */
export interface RequestTypeFieldProps {
    /** Currently selected request type */
    value: RequestType;
    /** Callback when request type changes */
    onChange: (value: RequestType) => void;
    /** Error message to display */
    error?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
}

/**
 * RequestTypeField component - Memoized to prevent unnecessary re-renders
 */
export function RequestTypeField({
    value,
    onChange,
    error,
    disabled = false,
}: RequestTypeFieldProps): React.ReactElement {
    const { t } = useI18n();
    const { getRequestTypeLabel } = useRequestTranslations();

    // Get all RequestType values as an array
    const requestTypes = Object.values(RequestType) as RequestType[];

    return (
        <div
            className={styles.field}
            data-testid="request-type-field"
        >
            <FormControl
                fullWidth
                error={!!error}
            >
                <FormLabel
                    id="request-type-label"
                    className={styles.fieldLabel}
                >
                    {t('requests.form.applicationType')}
                </FormLabel>
                <TextField
                    select
                    value={value}
                    onChange={(e): void =>
                        onChange(e.target.value as RequestType)
                    }
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    aria-labelledby="request-type-label"
                    inputProps={{
                        'aria-describedby': error
                            ? 'request-type-error'
                            : 'request-type-helper',
                    }}
                    data-testid="request-type-textfield"
                >
                    {requestTypes.map(
                        (typeValue: RequestType): React.ReactElement => {
                            // Get the enum key from the value
                            const key = Object.keys(RequestType).find(
                                (k): boolean =>
                                    RequestType[
                                        k as keyof typeof RequestType
                                    ] === typeValue,
                            ) as keyof typeof RequestType;
                            const label = getRequestTypeLabel(key);
                            return (
                                <MenuItem
                                    key={typeValue}
                                    value={typeValue}
                                    data-testid={`request-type-${typeValue.toLowerCase()}`}
                                >
                                    {label}
                                </MenuItem>
                            );
                        },
                    )}
                </TextField>
                {error && (
                    <FormHelperText
                        id="request-type-error"
                        error
                        data-testid="request-type-error-message"
                    >
                        {error}
                    </FormHelperText>
                )}
            </FormControl>
        </div>
    );
}

export default memo(RequestTypeField);
